import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ArticleImage, ImageImage, VideoImage } from "../agent-dashboard-user-detail/assets";
import { TextButton } from "../common/text-button";
import { PlusIcon } from "lucide-react";
import { TextInput } from "../common/text-input";
import { SearchActiveIcon, SearchBtnIcon, SearchIcon } from "../agent-dashboard/assets";
import { Dropdown } from "../ui/modal";
import { SearchFormData } from "@/manager/manager/agent-dashboard-company-asset-manager";
import { AddTemplateIcon } from "./assets";

interface SearchBarProps {
  searchFormData: SearchFormData;
  presetTemplateCount?: number;
  totalTemplateCount?: number;
  changeSearchForm: (key: string, value: SearchFormData[keyof SearchFormData]) => void;
  onAddClick?: () => void;
  onBatchClick?: () => void;
  onSearch: () => void;
  onReset: () => void;
}

export const SearchBar = ({
  searchFormData,
  changeSearchForm,
  onAddClick,
  onBatchClick,
  onSearch,
  onReset,
  presetTemplateCount,
  totalTemplateCount,
}: SearchBarProps) => {
  const { t } = useTranslation();

  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const indicatorRef = useRef<HTMLDivElement>(null)

  // 监听tab变化，更新底部背景位置
  useEffect(() => {
    const activeTabElement = tabRefs.current[searchFormData.type];
    const indicatorElement = indicatorRef.current;

    if (activeTabElement && indicatorElement) {
      const { left, width } = activeTabElement.getBoundingClientRect();
      const containerLeft = activeTabElement.parentElement?.getBoundingClientRect().left || 0;
      const offsetLeft = left - containerLeft;

      indicatorElement.style.transform = `translateX(${offsetLeft}px)`;
      indicatorElement.style.width = `${width}px`;
    }
  }, [searchFormData.type]);

  // Tab选项定义
  const tabs = [
    {
      key: 1,
      icon: <VideoImage className='w-5 h-5' />,
      label: t('agent_dashboard.company_asset.search_bar.type.video')
    },
    {
      key: 4,
      icon: <ImageImage className='w-5 h-5' />,
      label: t('agent_dashboard.company_asset.search_bar.type.post')
    },
    {
      key: 5,
      icon: <ArticleImage className='w-5 h-5' />,
      label: t('agent_dashboard.company_asset.search_bar.type.article')
    },
  ];

  const handleChangeTab = (tabKey: number) => {
    changeSearchForm('type', tabKey);
    onSearch();
  }

  return (
    <div className="w-full flex flex-col gap-5 mb-5">
      <div className="flex items-center justify-between">
        {/* type切换区域 */}
        <div className="relative">
          <div className="flex  overflow-x-auto scrollbar-hide gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                ref={(el) => (tabRefs.current[tab.key] = el)}
                className={cn(
                  'group px-2 py-1.5 text-sm font-medium transition-all whitespace-nowrap flex gap-[5px] text-[rgba(102,112,133,1)]',
                  'hover:text-[rgba(16,24,40,1)]',
                  searchFormData.type === tab.key && 'text-[rgba(16,24,40,1)] bg-[rgba(16,24,40,0.05)] rounded-[6px]',
                )}
                onClick={() => handleChangeTab(tab.key)}
              >
                <div className="max-w-max relative">
                  {tab.icon}
                  <div
                    className={cn(
                      "absolute inset-0 transition-all",
                      searchFormData.type !== tab.key ? 'bg-white/60' : '',
                      'group-hover:bg-transparent'
                    )}
                  />
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className="flex items-center justify-center gap-3">
          <div className="text-sm text-[rgba(102,112,133,1)] max-w-max space-x-1">
            <span>{t('agent_dashboard.company_asset.search_bar.add_tips')}</span>
            <span>{`${presetTemplateCount || 0}/${totalTemplateCount || 0}`}</span>
          </div>
          <TextButton
            label={t('agent_dashboard.company_asset.search_bar.batch_management')}
            className='!max-w-max !px-4 h-10 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-sm !text-[rgba(65,70,81,1)] whitespace-nowrap'
            onClick={onBatchClick}
          />
          <TextButton
            icon={<AddTemplateIcon className='w-5 h-5' />}
            label={t('agent_dashboard.company_asset.search_bar.add_template')}
            className='max-w-max !px-4  h-10 font-medium text-sm'
            onClick={onAddClick}
          />
        </div>
      </div>
      {/* 搜索区域 */}
      <div className="flex gap-3">
        <div className="flex flex-wrap gap-3">
          <div>
            {/* <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.content_management.search.conditional_filtering')}</label> */}
            <Dropdown
              value={searchFormData.searchType}
              placeholder={t('agent_dashboard.company_asset.search_bar.select_placeholder')}
              className='h-9'
              onValueChange={(value) => changeSearchForm('searchType', value)}
              width='11.3rem'
              options={[
                { value: 'template_id', label: t('agent_dashboard.company_asset.search_bar.filter.id') },
                { value: 'template_name', label: t('agent_dashboard.company_asset.search_bar.filter.name') },
              ]}
            />
          </div>
          <div className='flex items-end'>
            <TextInput
              value={searchFormData.searchValue}
              placeholder={t('agent_dashboard.company_asset.search_bar.search_placeholder')}
              className='w-[302px] h-9 flex-1 font-normal'
              prefixIcon={<SearchIcon className='w-5 h-5' />}
              prefixActiveIcon={<SearchActiveIcon className='w-5 h-5' />}
              onChange={(e) => changeSearchForm('searchValue', e.target.value)}
            />
          </div>
        </div>
        <div className='flex items-end space-x-3'>
          <TextButton
            label={t('agent_dashboard.user_management.search.reset')}
            className={cn(
              'max-w-max !px-5 h-9 bg-white',
              'font-semibold text-sm !text-[rgba(65,70,81,1)]',
              'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
            )}
            onClick={onReset}
          />
          <TextButton
            label={t('agent_dashboard.user_management.search.search')}
            icon={<SearchBtnIcon className='w-5 h-5' />}
            className={cn(
              '!w-[6.4rem] h-9 ',
              'text-white font-semibold text-sm',
            )}
            onClick={onSearch}
          />
        </div>
      </div>
    </div>
  )
}
